import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalProfile } from "../local/useLocalProfile";
import { AvatarPreview } from "../components/AvatarEditor";
import { clearProfile, saveProfile, updateName } from "../local/profile";
import { isGuest } from "../local/guestUser";
import { loadGameSettings, saveGameSettings } from "../local/gameSettings";
import { QualityPicker } from "../game/GameView";

type TabId = "general" | "audio" | "language" | "controls" | "account" | "privacy" | "about";

const TABS: { id: TabId; label: string; sub: string; icon: string; tone: string }[] = [
  { id: "general",  label: "General",  sub: "Game and app settings", icon: "⚙",  tone: "pink"   },
  { id: "audio",    label: "Audio",    sub: "Sound and voice",        icon: "🔊", tone: "blue"   },
  { id: "language", label: "Language", sub: "App language",           icon: "🌐", tone: "sky"    },
  { id: "controls", label: "Controls", sub: "How you play",           icon: "🎮", tone: "green"  },
  { id: "account",  label: "Account",  sub: "Profile and sign in",    icon: "👤", tone: "orange" },
  { id: "privacy",  label: "Privacy",  sub: "Privacy and safety",     icon: "🛡", tone: "purple" },
  { id: "about",    label: "About",    sub: "App info and help",      icon: "ⓘ", tone: "slate"  },
];

export function LocalSettings() {
  const { profile, setProfile } = useLocalProfile();
  const nav = useNavigate();
  const [tab, setTab] = useState<TabId>("general");
  const [settings, setSettings] = useState(() => loadGameSettings());
  const [voiceSpeed, setVoiceSpeed] = useState<"slow" | "normal" | "fast">("normal");
  const [haptic, setHaptic] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [progressHints, setProgressHints] = useState(true);
  const [dailyReminder, setDailyReminder] = useState(false);
  const [name, setName] = useState(profile?.displayName ?? "");

  useEffect(() => {
    setSettings(loadGameSettings());
    if (typeof document !== "undefined") setFullscreen(!!document.fullscreenElement);
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  if (!profile) {
    nav("/", { replace: true });
    return null;
  }

  const guest = isGuest(profile);
  const playerLevel = Math.max(1, profile.lifetimeRuns ?? 1);

  const writeSettings = (patch: Partial<typeof settings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveGameSettings(next);
  };

  const setLearnLanguage = (lang: "en" | "vi") => {
    const next = { ...profile, learnLanguage: lang };
    saveProfile(next);
    setProfile(next);
  };

  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === profile.displayName) return;
    setProfile(updateName(profile, trimmed));
  };

  const reset = () => {
    if (!confirm("Reset character? You'll lose your coins, upgrades, and progress.")) return;
    clearProfile();
    setProfile(null);
    nav("/", { replace: true });
  };

  const signOut = () => {
    if (!confirm("Sign out? Your progress stays saved on the server — log back in with your 4-digit code.")) return;
    clearProfile();
    setProfile(null);
    nav("/", { replace: true });
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
      // State is synced by the fullscreenchange listener above.
    } catch { /* iOS Safari etc. — ignore */ }
  };

  return (
    <div className="settings-v2-page">
      {/* Top bar */}
      <header className="settings-v2-top">
        <button className="settings-v2-back" onClick={() => nav("/")} aria-label="Back">←</button>
        <div className="settings-v2-titleblock">
          <h1>Settings</h1>
        </div>
        <div className="settings-v2-hud">
          <div className="settings-v2-hudcard">
            <span className="settings-v2-hudicon settings-v2-coin" aria-hidden>●</span>
            <span className="settings-v2-hudmeta">
              <strong>{profile.coins}</strong>
              <small>Coins</small>
            </span>
            <span className="settings-v2-hudplus" aria-hidden>+</span>
          </div>
          <div className="settings-v2-hudcard">
            <span className="settings-v2-hudicon settings-v2-level" aria-hidden>◆</span>
            <span className="settings-v2-hudmeta">
              <strong>{playerLevel}</strong>
              <small>Level</small>
            </span>
          </div>
          <button
            className="settings-v2-hudcard settings-v2-hudname"
            onClick={() => nav("/character")}
          >
            <span className="settings-v2-hudface" aria-hidden>
              {guest ? "👤" : <AvatarPreview cfg={profile.avatarConfig} />}
            </span>
            <span className="settings-v2-hudmeta">
              <strong>{profile.displayName}</strong>
              <small>View Profile</small>
            </span>
            <span className="settings-v2-hudchev" aria-hidden>›</span>
          </button>
        </div>
      </header>

      <div className="settings-v2-grid">
        {/* Sidebar */}
        <aside className="settings-v2-side">
          <nav className="settings-v2-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`settings-v2-tab settings-v2-tab-${t.tone} ${tab === t.id ? "is-active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                <span className="settings-v2-tabicon" aria-hidden>{t.icon}</span>
                <span className="settings-v2-tabtext">
                  <strong>{t.label}</strong>
                  <small>{t.sub}</small>
                </span>
              </button>
            ))}
          </nav>
          <div className="settings-v2-version">Word Runner v1.0.0</div>
        </aside>

        {/* Main panel */}
        <main className="settings-v2-main">
          <div className="settings-v2-panel">
            <header className="settings-v2-panelhead">
              <span className={`settings-v2-panelicon settings-v2-panelicon-${TABS.find((t) => t.id === tab)?.tone}`} aria-hidden>
                {TABS.find((t) => t.id === tab)?.icon}
              </span>
              <div>
                <h2>{TABS.find((t) => t.id === tab)?.label}</h2>
                <p>{tabSubtitle(tab)}</p>
              </div>
            </header>

            {tab === "general" && (
              <>
                <div className="settings-v2-sliders">
                  <SliderRow
                    icon="🎵" tone="pink"
                    title="Music" sub="Background music volume"
                    value={settings.musicVolume}
                    onChange={(v) => writeSettings({ musicVolume: v })}
                  />
                  <SliderRow
                    icon="🔊" tone="blue"
                    title="Sound Effects" sub="Game sounds volume"
                    value={settings.audioVolume}
                    onChange={(v) => writeSettings({ audioVolume: v })}
                  />
                  <SliderRow
                    icon="🎤" tone="purple"
                    title="Voice" sub="Character voices and instructions"
                    value={1}
                    onChange={() => {}}
                  />
                  <div className="settings-v2-row settings-v2-pillrow">
                    <span className="settings-v2-rowicon settings-v2-rowicon-green" aria-hidden>⏱</span>
                    <div className="settings-v2-rowtext">
                      <strong>Voice Speed</strong>
                      <small>How fast the voice speaks</small>
                    </div>
                    <div className="settings-v2-pillgroup">
                      {(["slow", "normal", "fast"] as const).map((s) => (
                        <button
                          key={s}
                          className={`settings-v2-pill ${voiceSpeed === s ? "is-active" : ""}`}
                          onClick={() => setVoiceSpeed(s)}
                        >
                          {s[0]!.toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="settings-v2-toggles">
                  <ToggleRow icon="🎵" tone="pink"   title="Music"              sub="Theme music in menus & podiums"        on={settings.musicEnabled} onChange={(v) => writeSettings({ musicEnabled: v })} />
                  <ToggleRow icon="⭐" tone="gold"   title="Haptic Feedback"    sub="Vibration on actions and interactions" on={haptic}        onChange={setHaptic} />
                  <ToggleRow icon="⛶"  tone="blue"   title="Fullscreen Mode"    sub="Use the whole screen for gameplay"     on={fullscreen}    onChange={() => toggleFullscreen()} />
                  <ToggleRow icon="📊" tone="purple" title="Show Progress Hints" sub="Show tips and hints during levels"     on={progressHints} onChange={setProgressHints} />
                  <ToggleRow icon="📈" tone="sky"    title="Show FPS"           sub="On-screen frame-rate counter (perf check)" on={settings.showFps} onChange={(v) => writeSettings({ showFps: v })} />
                  <ToggleRow icon="🕒" tone="sky"    title="Daily Reminder"     sub="Get reminded to play and learn"        on={dailyReminder} onChange={setDailyReminder} />
                </div>

                <div className="settings-v2-row settings-v2-pillrow">
                  <span className="settings-v2-rowicon settings-v2-rowicon-purple" aria-hidden>🎨</span>
                  <div className="settings-v2-rowtext">
                    <strong>Graphics Quality</strong>
                    <small>
                      Lower this on older tablets if the game runs slow.
                      <em style={{ display: "block", marginTop: 4, color: "var(--wr-text-muted)" }}>
                        Low disables shadows, reflections + ambient
                        particles. Change takes effect when you start
                        the next level.
                      </em>
                    </small>
                  </div>
                  <QualityPicker />
                </div>

                <div className="settings-v2-actions">
                  <button className="settings-v2-action" onClick={reset}>
                    <span className="settings-v2-actionicon settings-v2-actionicon-blue" aria-hidden>↻</span>
                    <span className="settings-v2-actiontext">
                      <strong>Reset Progress</strong>
                      <small>This will clear all progress</small>
                    </span>
                  </button>
                  <button className="settings-v2-action">
                    <span className="settings-v2-actionicon settings-v2-actionicon-sky" aria-hidden>☁</span>
                    <span className="settings-v2-actiontext">
                      <strong>Cloud Save</strong>
                      <small>Last saved: <span style={{ color: "#22c55e" }}>Just now</span></small>
                    </span>
                  </button>
                  <button className="settings-v2-action settings-v2-action-danger" onClick={reset}>
                    <span className="settings-v2-actionicon settings-v2-actionicon-danger" aria-hidden>🗑</span>
                    <span className="settings-v2-actiontext">
                      <strong>Delete All Data</strong>
                      <small>This cannot be undone</small>
                    </span>
                  </button>
                </div>
              </>
            )}

            {tab === "audio" && (
              <>
                <div className="settings-v2-toggles">
                  <ToggleRow icon="🎵" tone="pink" title="Music" sub="Theme music in menus & podiums" on={settings.musicEnabled} onChange={(v) => writeSettings({ musicEnabled: v })} />
                </div>
                <div className="settings-v2-sliders">
                  <SliderRow icon="🎚" tone="pink"   title="Music Volume"  sub="Theme music level"  value={settings.musicVolume} onChange={(v) => writeSettings({ musicVolume: v })} />
                  <SliderRow icon="🔊" tone="blue"   title="Sound Effects" sub="Game sounds volume" value={settings.audioVolume} onChange={(v) => writeSettings({ audioVolume: v })} />
                </div>
              </>
            )}

            {tab === "language" && (
              <div className="settings-v2-toggles">
                <button
                  className={`settings-v2-row ${(profile.learnLanguage ?? "en") === "en" ? "is-on" : ""}`}
                  onClick={() => setLearnLanguage("en")}
                >
                  <span className="settings-v2-rowicon" aria-hidden>🇬🇧</span>
                  <div className="settings-v2-rowtext">
                    <strong>English</strong>
                    <small>Learn English</small>
                  </div>
                </button>
                <button
                  className={`settings-v2-row ${profile.learnLanguage === "vi" ? "is-on" : ""}`}
                  onClick={() => setLearnLanguage("vi")}
                >
                  <span className="settings-v2-rowicon" aria-hidden>🇻🇳</span>
                  <div className="settings-v2-rowtext">
                    <strong>Tiếng Việt</strong>
                    <small>Học tiếng Việt</small>
                  </div>
                </button>
              </div>
            )}

            {tab === "controls" && (
              <div className="settings-v2-toggles">
                <ToggleRow
                  icon="🧸" tone="gold"
                  title="Kids Assist Mode"
                  sub="Auto-runs and auto-jumps. Steering still up to the player."
                  on={settings.assistMode}
                  onChange={(v) => writeSettings({ assistMode: v })}
                />
                <ToggleRow icon="⛶" tone="blue" title="Fullscreen Mode" sub="Use the whole screen for gameplay" on={fullscreen} onChange={(v) => { setFullscreen(v); toggleFullscreen(); }} />
              </div>
            )}

            {tab === "account" && (
              <div className="settings-v2-toggles">
                <div className="settings-v2-row">
                  <span className="settings-v2-rowicon settings-v2-rowicon-orange" aria-hidden>👤</span>
                  <div className="settings-v2-rowtext" style={{ flex: 1 }}>
                    <strong>Display Name</strong>
                    <input
                      className="settings-v2-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={20}
                    />
                  </div>
                  <button className="settings-v2-pill is-active" onClick={saveName}>Save</button>
                </div>
                {profile.code && (
                  <div className="settings-v2-row">
                    <span className="settings-v2-rowicon settings-v2-rowicon-gold" aria-hidden>🔑</span>
                    <div className="settings-v2-rowtext">
                      <strong>Player code</strong>
                      <small>Use this to log in from another device</small>
                    </div>
                    <code className="settings-v2-code">{profile.code}</code>
                  </div>
                )}
                <button className="settings-v2-action settings-v2-action-danger" onClick={signOut}>
                  <span className="settings-v2-actionicon settings-v2-actionicon-danger" aria-hidden>🚪</span>
                  <span className="settings-v2-actiontext">
                    <strong>Sign Out</strong>
                    <small>Removes this character from the device</small>
                  </span>
                </button>
              </div>
            )}

            {tab === "privacy" && (
              <p className="settings-v2-prose">
                Word Runner stores progress on this device. Cloud Save (if signed in)
                only stores progress, character look, and your display name. No ads,
                no tracking, no third-party analytics.
              </p>
            )}

            {tab === "about" && (
              <p className="settings-v2-prose">
                Word Runner is a tablet-first runner game for kids learning English.
                Pictures by <a href="https://openmoji.org" target="_blank" rel="noreferrer">OpenMoji</a> (CC BY-SA 4.0).
                Voice by Mistral AI.
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function tabSubtitle(tab: TabId): string {
  switch (tab) {
    case "general":  return "Customize your experience";
    case "audio":    return "Music, voice, and game sounds";
    case "language": return "Choose which language to learn";
    case "controls": return "Set how you play";
    case "account":  return "Your name, code, and sign-out";
    case "privacy":  return "Data and safety";
    case "about":    return "Credits and version info";
  }
}

function SliderRow({ icon, tone, title, sub, value, onChange }: {
  icon: string; tone: string; title: string; sub: string; value: number; onChange: (v: number) => void;
}) {
  const pct = Math.round(value * 100);
  return (
    <div className="settings-v2-row settings-v2-sliderrow">
      <span className={`settings-v2-rowicon settings-v2-rowicon-${tone}`} aria-hidden>{icon}</span>
      <div className="settings-v2-rowtext">
        <strong>{title}</strong>
        <small>{sub}</small>
      </div>
      <div className="settings-v2-sliderwrap">
        <input
          type="range"
          min={0} max={100} step={1}
          value={pct}
          onChange={(e) => onChange(Number(e.target.value) / 100)}
          className={`settings-v2-slider settings-v2-slider-${tone}`}
          style={{ ["--p" as unknown as string]: `${pct}%` }}
        />
        <span className="settings-v2-slidernum">{pct}%</span>
      </div>
    </div>
  );
}

function ToggleRow({ icon, tone, title, sub, on, onChange }: {
  icon: string; tone: string; title: string; sub: string; on: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="settings-v2-row">
      <span className={`settings-v2-rowicon settings-v2-rowicon-${tone}`} aria-hidden>{icon}</span>
      <div className="settings-v2-rowtext">
        <strong>{title}</strong>
        <small>{sub}</small>
      </div>
      <button
        className={`settings-v2-toggle ${on ? "is-on" : ""}`}
        onClick={() => onChange(!on)}
        aria-pressed={on}
        aria-label={title}
      >
        <span className="settings-v2-toggle-knob" />
      </button>
    </div>
  );
}
